import moment from 'moment'

/* =========================================================
   Date format constants (unchanged)
   ========================================================= */
export const defaultDateFormat = 'YYYY-MM-DD'
export const defaultTimeFormat = 'H:mm'
export const defaultLongDateFormat = 'dddd, YYYY-MM-DD, H:mm'
export const praLongDateFormat = 'dddd, DD MMMM YYYY'
export const praLongDateTimeFormat = 'dddd, DD MMMM YYYY, H:mm'

/* =========================================================
   Generic helpers (unchanged)
   ========================================================= */
export const jsonToQueryString = (payload = {}) =>
  Object.keys(payload)
    .filter(key => payload[key] !== null && payload[key] !== undefined)
    .map((key, index) => `${!index ? '?' : ''}${key}=${encodeURIComponent(payload[key])}`)
    .join('&')

export const formatFileUrl = (fileId, params) => {
  if (params) {
    if (params.ing) {
      return `${process.env.REACT_APP_API_URL}/file/ing/${encodeURI(params.fileName)}`
    } else if (params?.hash?.temp) {
      return `${process.env.REACT_APP_API_URL}/file/temp/${fileId}`
    }
  }
  return `${process.env.REACT_APP_API_URL}/file/${fileId}`
}

export const groupNumber = num => {
  const tokens = num.toString().split('.')
  return `${tokens[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')}${tokens[1] ? `.${tokens[1]}` : ''}`
}

export const formatNumber = (num = 0, decimal = 0) =>
  groupNumber(parseFloat(num).toFixed(decimal))

export const formatCurrency = (num = 0, decimal = 2, symbol = '€') =>
  `${symbol} ${formatNumber(num, decimal)}`

export const formatNameUrl = value =>
  value ? value.split(' ').join('-').toLowerCase() : value

/* =========================================================
   SAFE CORE DATE FORMATTERS (Moment – EN ONLY)
   ========================================================= */
export const formatDate = (dateTime, format = defaultDateFormat) =>
  formatDateTime(dateTime, format)

export const formatDateTime = (
  dateTime,
  format = `${defaultDateFormat} ${defaultTimeFormat}`
) => {
  if (!dateTime) return ''

  const m = moment(dateTime)
  if (!m.isValid()) return ''

  // LOCKED to English → no locale bleed, no surprises
  return m.locale('en').format(format)
}

/* =========================================================
   Parsers (unchanged – strict & safe)
   ========================================================= */
export const parseDate = (dateTime, format = defaultDateFormat) =>
  parseDateTime(dateTime, format)

export const parseDateTime = (
  dateTime,
  format = `${defaultDateFormat} ${defaultTimeFormat}`
) => {
  if (!dateTime) return ''

  const m = moment(dateTime, format, true)
  if (!m.isValid()) return ''

  return m.toDate()
}

/* =========================================================
   STRING UTILITIES (unchanged)
   ========================================================= */
export const formatString = function (value, ...args) {
  if (!value || !args) return value

  return value.replace(/{(\d+)}/g, (_, index) => {
    const argValue = args[index]
    return argValue !== null && argValue !== undefined ? argValue : ''
  })
}

export const formatResourceString = function (value, data) {
  if (!value || !data) return value

  return value.replace(/{{(.*?)}}/g, (_, key) => {
    const argValue = data[key.trim()]
    return argValue !== null && argValue !== undefined ? argValue : ''
  })
}

/* =========================================================
   LANGUAGE-AWARE UI FORMATTER (Intl – SAFE)
   ========================================================= */
const languageToLocale = {
  en: 'en-GB',
  nl: 'nl-NL',
  de: 'de-DE',
  fr: 'fr-FR',
  pl: 'pl-PL'
}

/**
 * UI-only date formatter
 * ✔ No Moment locale
 * ✔ No parsing side effects
 * ✔ Works with JS Date / ISO / timestamp
 */
export const formatDateTimeByLanguage = (
  dateTime,
  format,
  lang = 'en'
) => {
  if (!dateTime) return ''

  const date =
    dateTime instanceof Date
      ? dateTime
      : new Date(dateTime)

  if (isNaN(date.getTime())) return ''

  const normalizedLang = String(lang).toLowerCase()

  const locale = languageToLocale[normalizedLang] || 'en-GB'

  const showTime = format?.includes('H') || format?.includes('m')

  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: showTime ? '2-digit' : undefined,
    minute: showTime ? '2-digit' : undefined,
    hour12: false
  }).format(date)
}

export const formatDateByLanguage = (dateTime, format, lang) =>
  formatDateTimeByLanguage(dateTime, format, lang)
